import { z } from "zod";
import { conversationExecutionSchema } from "./conversations.js";
import { continuityIdentitySchema, continuitySessionSchema, workspaceStorageSchema } from "./continuity.js";
import { appIdSchema, isoTimestampSchema, runIdSchema, userIdSchema, } from "./identifiers.js";
import { runtimeProviderMaterialSchema } from "./providers.js";
import { appOwnerCredentialRef, appOwnerCredentialRefSchema, agentAppMutationJournalGenerationSchema, agentAppMutationJournalStateSchema, runtimeStartSchema, } from "./runtime.js";
/**
 * Control-plane operations exposed to a hosted app agent. These names match
 * the versioned control-plane operation identifiers, rather than HTTP paths,
 * so policy remains stable when routing changes.
 */
export const openCloudRunActionSchema = z.enum([
    "listApps",
    "getApp",
    "configureApp",
    "createDraft",
    "listDrafts",
    "getDraft",
    "listDraftFiles",
    "readDraftFiles",
    "applyDraftChanges",
    "diffDraft",
    "validateDraft",
    "discardDraft",
    "startDevSession",
    "getDevSession",
    "applyDevRevision",
    "requestDevApp",
    "mutateDevData",
    "invokeDevFunction",
    "verifyDevSession",
    "listDevInvocations",
    "listDevReceipts",
    "promoteDevRevision",
    "stopDevSession",
    "verifyApp",
    "getVerification",
    "listDeployments",
    "getDeployment",
    "getOperation",
    "listSecrets",
    "generateSecret",
    "createSecretEntryLink",
    "listBackups",
    "createBackup",
    "listCronInvocations",
    "invokeCron",
    "getAgentFeed",
    "listAlertRules",
    "putAlertRule",
    "deleteAlertRule",
    "queryLogs",
    "queryMetrics",
    "getUsage",
]);
export const ROOT_OPEN_CLOUD_ACTIONS = openCloudRunActionSchema.options;
/**
 * Model-readable root authority excludes the trusted production release
 * boundary. Runtime policy and the control plane both enforce this list.
 */
export const DEVELOPMENT_OPEN_CLOUD_ACTIONS = [
    "listApps",
    "getApp",
    "createDraft",
    "listDrafts",
    "getDraft",
    "listDraftFiles",
    "readDraftFiles",
    "applyDraftChanges",
    "diffDraft",
    "validateDraft",
    "discardDraft",
    "startDevSession",
    "getDevSession",
    "applyDevRevision",
    "requestDevApp",
    "mutateDevData",
    "invokeDevFunction",
    "verifyDevSession",
    "listDevInvocations",
    "listDevReceipts",
    "stopDevSession",
    "getVerification",
    "listDeployments",
    "getDeployment",
    "getOperation",
    "listSecrets",
    "listBackups",
    "listCronInvocations",
    "getAgentFeed",
    "listAlertRules",
    "queryLogs",
    "queryMetrics",
    "getUsage",
];
/**
 * Unattended repair grants can inspect, develop, validate, promote an exact
 * verified revision, and confirm recovery. They cannot manage secrets,
 * backups, billing-like resources, or invoke production jobs.
 */
export const REPAIR_OPEN_CLOUD_ACTIONS = [
    "listApps",
    "getApp",
    "createDraft",
    "listDrafts",
    "getDraft",
    "listDraftFiles",
    "readDraftFiles",
    "applyDraftChanges",
    "diffDraft",
    "validateDraft",
    "discardDraft",
    "startDevSession",
    "getDevSession",
    "applyDevRevision",
    "requestDevApp",
    "mutateDevData",
    "invokeDevFunction",
    "verifyDevSession",
    "listDevInvocations",
    "listDevReceipts",
    "promoteDevRevision",
    "stopDevSession",
    "verifyApp",
    "getVerification",
    "listDeployments",
    "getDeployment",
    "getOperation",
    "listSecrets",
    "listBackups",
    "listCronInvocations",
    "getAgentFeed",
    "listAlertRules",
    "putAlertRule",
    "deleteAlertRule",
    "queryLogs",
    "queryMetrics",
    "getUsage",
];
/** Child sessions can inspect and test, but cannot mutate or promote. */
export const CHILD_OPEN_CLOUD_ACTIONS = [
    "listApps",
    "getApp",
    "listDrafts",
    "getDraft",
    "listDraftFiles",
    "readDraftFiles",
    "diffDraft",
    "getDevSession",
    "requestDevApp",
    "listDevInvocations",
    "listDevReceipts",
    "getVerification",
    "listDeployments",
    "getDeployment",
    "getOperation",
    "listSecrets",
    "listBackups",
    "listCronInvocations",
    "getAgentFeed",
    "listAlertRules",
    "queryLogs",
    "queryMetrics",
    "getUsage",
];
export const agentRunGrantKindSchema = z.enum(["root", "child"]);
export const agentRunGrantPurposeSchema = z.enum(["interactive", "repair"]);
export const agentRunGrantProfileSchema = z.enum(["full", "development"]);
export const agentRunGrantRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    userId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    kind: agentRunGrantKindSchema,
    purpose: agentRunGrantPurposeSchema.default("interactive"),
    profile: agentRunGrantProfileSchema.default("full"),
    expiresInSeconds: z.number().int().min(60).max(86_400),
})
    .strict()
    .superRefine((value, context) => {
    if (value.kind === "root" && value.rootRunId !== value.runId) {
        context.addIssue({
            code: "custom",
            path: ["rootRunId"],
            message: "A root grant must use its run as rootRunId",
        });
    }
    if (value.kind === "child" && value.rootRunId === value.runId) {
        context.addIssue({
            code: "custom",
            path: ["rootRunId"],
            message: "A child grant must identify a different root run",
        });
    }
    if (value.kind === "child" && value.profile !== "full") {
        context.addIssue({
            code: "custom",
            path: ["profile"],
            message: "Child grants use the bounded child profile",
        });
    }
});
export const agentRunGrantRenewalRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    userId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    expiresInSeconds: z.number().int().min(60).max(86_400),
})
    .strict();
const agentAppOwnerCredentialIdentitySchema = z
    .object({
    schemaVersion: z.literal(1),
    delegatedByUserId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
})
    .strict();
export const agentAppMutationJournalRecordSchema = z
    .object({
    schemaVersion: z.literal(1),
    appId: appIdSchema,
    generation: agentAppMutationJournalGenerationSchema,
    state: agentAppMutationJournalStateSchema,
})
    .strict();
const agentAppMutationJournalRequestFields = {
    schemaVersion: z.literal(1),
    delegatedByUserId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    credentialId: z.uuid(),
};
export const agentAppMutationJournalAllocateRequestSchema = z
    .object(agentAppMutationJournalRequestFields)
    .strict()
    .superRefine(requireRootOwnerCredential);
export const agentAppMutationJournalTransitionRequestSchema = z
    .object({
    ...agentAppMutationJournalRequestFields,
    generation: agentAppMutationJournalGenerationSchema,
})
    .strict()
    .superRefine(requireRootOwnerCredential);
/**
 * Internal request to create the first credential in an app-owner authority
 * family. Unlike legacy run grants, this credential has no action profile: the
 * exact app is the authorization boundary.
 */
export const agentAppOwnerCredentialMintRequestSchema = agentAppOwnerCredentialIdentitySchema
    .extend({
    expiresInSeconds: z.number().int().min(60).max(86_400),
})
    .strict()
    .superRefine(requireRootOwnerCredential);
/** Internal request to replace one active app-owner credential generation. */
export const agentAppOwnerCredentialRotateRequestSchema = agentAppOwnerCredentialMintRequestSchema;
/** Internal request to revoke one app-owner credential family. */
export const agentAppOwnerCredentialRevokeRequestSchema = agentAppOwnerCredentialIdentitySchema.superRefine(requireRootOwnerCredential);
function requireRootOwnerCredential(value, context) {
    if (value.rootRunId !== value.runId) {
        context.addIssue({
            code: "custom",
            path: ["runId"],
            message: "An app-owner credential belongs to its root run",
        });
    }
}
export const agentAppOwnerCredentialViewSchema = z
    .object({
    schemaVersion: z.literal(1),
    credentialKind: z.literal("agent_app_owner"),
    credentialId: z.uuid(),
    familyId: z.uuid(),
    generation: z.number().int().positive(),
    rotatedFromCredentialId: z.uuid().nullable(),
    delegatedByUserId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    expiresAt: isoTimestampSchema,
    revokedAt: isoTimestampSchema.nullable(),
})
    .strict();
/** Sensitive response restricted to the control-plane/Agent-service boundary. */
export const agentAppOwnerCredentialSecretSchema = agentAppOwnerCredentialViewSchema.extend({
    token: z.string().startsWith("oc_owner_").min(32).max(512),
    appUrl: z.url().nullable(),
});
/**
 * Current control-plane response used by Agent-service paths that need an
 * app's owner and AI credential policy. Producers validate the complete
 * versioned response; consumers that need only a subset should explicitly
 * pick and strip the fields they consume so additive fields remain safe.
 */
export const agentAppOwnerPolicyResponseSchema = z
    .object({
    schemaVersion: z.literal(1),
    appId: appIdSchema,
    ownerUserId: userIdSchema,
    appName: z.string().trim().min(1).max(200).nullable().default(null),
    appSlug: z.string().trim().min(1).max(200).nullable().default(null),
    activeDeploymentId: z.uuid().nullable().default(null),
    aiCredentialSource: z.enum(["owner", "user", "platform"]),
    userAiConsentGranted: z.boolean(),
    // Older producers do not report personal consent; absence never grants it.
    personalAiConsentGranted: z.boolean().default(false),
})
    .strict();
export const agentRunGrantViewSchema = z
    .object({
    schemaVersion: z.literal(1),
    id: z.uuid(),
    userId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    kind: agentRunGrantKindSchema,
    profile: agentRunGrantProfileSchema.default("full"),
    allowedActions: z.array(openCloudRunActionSchema).min(1),
    expiresAt: isoTimestampSchema,
    revokedAt: isoTimestampSchema.nullable(),
})
    .strict();
/** Sensitive response restricted to the control-plane/agent-service boundary. */
export const agentRunGrantSecretSchema = agentRunGrantViewSchema.extend({
    token: z.string().min(32).max(512),
    appUrl: z.url().nullable(),
});
/** Sensitive file mounted read-only into one root runtime. */
export const runtimeAuthorityMaterialSchema = z
    .object({
    schemaVersion: z.literal(1),
    grantId: z.uuid(),
    userId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    kind: agentRunGrantKindSchema,
    profile: agentRunGrantProfileSchema.default("full"),
    apiUrl: z.url(),
    appUrl: z.url().nullable(),
    token: z.string().min(32).max(512),
    allowedActions: z.array(openCloudRunActionSchema).min(1),
    expiresAt: isoTimestampSchema,
})
    .strict();
/** Sensitive single-session material mounted into one app-owner v3 runtime. */
export const runtimeAppOwnerCredentialMaterialSchema = z
    .object({
    schemaVersion: z.literal(1),
    credentialKind: z.literal("agent_app_owner"),
    credentialId: z.uuid(),
    familyId: z.uuid(),
    generation: z.number().int().positive(),
    rotatedFromCredentialId: z.uuid().nullable(),
    delegatedByUserId: userIdSchema,
    appId: appIdSchema,
    rootRunId: runIdSchema,
    runId: runIdSchema,
    apiUrl: z.url(),
    appUrl: z.url().nullable(),
    token: z.string().startsWith("oc_owner_").min(32).max(512),
    expiresAt: isoTimestampSchema,
})
    .strict()
    .superRefine(requireRootOwnerCredential);
/**
 * Sensitive fixed-shape handoff to the Docker-socket holder. It deliberately
 * contains no arbitrary paths, image, command, mount, network, or Docker
 * options.
 */
const legacyRuntimeMaterialPrepareSchema = z
    .object({
    workspaceStorage: workspaceStorageSchema.optional(),
    continuitySession: continuitySessionSchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("legacy_split_v1").default("legacy_split_v1"),
    start: runtimeStartSchema,
    provider: z
        .object({
        manifest: runtimeProviderMaterialSchema,
        authContent: z
            .string()
            .min(2)
            .max(1024 * 1024),
    })
        .strict(),
    authority: runtimeAuthorityMaterialSchema,
    // Present for AI-SDK Codex roots. This is the only authority material
    // used to create the model-readable CLI session.
    modelAuthority: runtimeAuthorityMaterialSchema.optional(),
    appOwnerCredential: z.never().optional(),
    callbackToken: z.string().min(32).max(4_096),
})
    .strict()
    .superRefine((value, context) => {
    if (value.start.authorityMode !== value.authorityMode) {
        context.addIssue({
            code: "custom",
            path: ["start", "authorityMode"],
            message: "Runtime start and material authority modes must match",
        });
    }
    if (value.start.authorityGrantRef !== `grant:${value.authority.grantId}`) {
        context.addIssue({
            code: "custom",
            path: ["authority", "grantId"],
            message: "Trusted authority differs from its runtime reference",
        });
    }
    if (value.authority.userId !== value.start.userId ||
        value.authority.appId !== value.start.appId ||
        value.authority.rootRunId !==
            (value.start.kind === "root"
                ? value.start.runId
                : value.start.parentRunId) ||
        value.authority.runId !== value.start.runId ||
        value.authority.kind !== value.start.kind) {
        context.addIssue({
            code: "custom",
            path: ["authority"],
            message: "Trusted authority differs from the runtime identity",
        });
    }
    const modelRef = value.start.modelAuthorityGrantRef;
    const usesSplitAuthority = value.start.kind === "root" &&
        value.start.engine.adapter === "codex" &&
        [
            "opencloud-codex-ai-sdk-harness-v1",
            "opencloud-codex-ai-sdk-harness-v2",
        ].includes(value.start.engine.harnessVersion ?? "");
    if (Boolean(value.modelAuthority) !== usesSplitAuthority) {
        context.addIssue({
            code: "custom",
            path: ["modelAuthority"],
            message: "AI-SDK Codex material requires split model authority",
        });
    }
    if (value.authority.profile !== "full") {
        context.addIssue({
            code: "custom",
            path: ["authority", "profile"],
            message: "Trusted runtime authority must use the full profile",
        });
    }
    if (Boolean(modelRef) !== Boolean(value.modelAuthority)) {
        context.addIssue({
            code: "custom",
            path: ["modelAuthority"],
            message: "Model authority and its runtime reference must be present together",
        });
    }
    if (value.modelAuthority &&
        modelRef !== `grant:${value.modelAuthority.grantId}`) {
        context.addIssue({
            code: "custom",
            path: ["modelAuthority", "grantId"],
            message: "Model authority differs from its runtime reference",
        });
    }
    if (value.modelAuthority &&
        value.modelAuthority.grantId === value.authority.grantId) {
        context.addIssue({
            code: "custom",
            path: ["modelAuthority", "grantId"],
            message: "Trusted and model authority grants must be distinct",
        });
    }
    if (value.modelAuthority?.profile !== undefined) {
        if (value.modelAuthority.profile !== "development") {
            context.addIssue({
                code: "custom",
                path: ["modelAuthority", "profile"],
                message: "Model authority must use the development profile",
            });
        }
        if (JSON.stringify(value.modelAuthority.allowedActions) !==
            JSON.stringify(DEVELOPMENT_OPEN_CLOUD_ACTIONS)) {
            context.addIssue({
                code: "custom",
                path: ["modelAuthority", "allowedActions"],
                message: "Model authority must use the exact development action profile",
            });
        }
    }
    if (value.modelAuthority) {
        const trusted = value.authority;
        const model = value.modelAuthority;
        if (model.userId !== trusted.userId ||
            model.appId !== trusted.appId ||
            model.rootRunId !== trusted.rootRunId ||
            model.runId !== trusted.runId ||
            model.kind !== trusted.kind ||
            model.apiUrl !== trusted.apiUrl ||
            model.appUrl !== trusted.appUrl) {
            context.addIssue({
                code: "custom",
                path: ["modelAuthority"],
                message: "Model authority differs from the trusted run identity",
            });
        }
    }
});
const appOwnerRuntimeMaterialPrepareSchema = z
    .object({
    workspaceStorage: workspaceStorageSchema.optional(),
    continuitySession: continuitySessionSchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("app_owner_v1"),
    start: runtimeStartSchema,
    provider: z
        .object({
        manifest: runtimeProviderMaterialSchema,
        authContent: z
            .string()
            .min(2)
            .max(1024 * 1024),
    })
        .strict(),
    appOwnerCredential: runtimeAppOwnerCredentialMaterialSchema,
    authority: z.never().optional(),
    modelAuthority: z.never().optional(),
    callbackToken: z.string().min(32).max(4_096),
})
    .strict()
    .superRefine((value, context) => {
    if (value.start.authorityMode !== value.authorityMode) {
        context.addIssue({
            code: "custom",
            path: ["start", "authorityMode"],
            message: "Runtime start and material authority modes must match",
        });
    }
    const material = value.appOwnerCredential;
    if (value.start.appOwnerCredentialRef !==
        appOwnerCredentialRef(material.credentialId, material.generation)) {
        context.addIssue({
            code: "custom",
            path: ["appOwnerCredential", "credentialId"],
            message: "App-owner credential differs from its runtime reference",
        });
    }
    if (material.delegatedByUserId !== (value.start.conversationExecution?.actorUserId ?? value.start.userId) ||
        material.appId !== value.start.appId ||
        material.rootRunId !== value.start.runId ||
        material.runId !== value.start.runId) {
        context.addIssue({
            code: "custom",
            path: ["appOwnerCredential"],
            message: "App-owner credential differs from the root runtime identity",
        });
    }
});
const conversationOnlyRuntimeMaterialPrepareSchema = z
    .object({
    workspaceStorage: workspaceStorageSchema.optional(),
    continuitySession: continuitySessionSchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("conversation_only_v1"),
    start: runtimeStartSchema,
    provider: z
        .object({
        manifest: runtimeProviderMaterialSchema,
        authContent: z
            .string()
            .min(2)
            .max(1024 * 1024),
    })
        .strict(),
    authority: z.never().optional(),
    modelAuthority: z.never().optional(),
    appOwnerCredential: z.never().optional(),
    callbackToken: z.string().min(32).max(4_096),
})
    .strict()
    .superRefine((value, context) => {
    if (value.start.authorityMode !== value.authorityMode) {
        context.addIssue({
            code: "custom",
            path: ["start", "authorityMode"],
            message: "Runtime start and material authority modes must match",
        });
    }
});
export const runtimeMaterialPrepareSchema = z.union([
    legacyRuntimeMaterialPrepareSchema,
    conversationOnlyRuntimeMaterialPrepareSchema,
    appOwnerRuntimeMaterialPrepareSchema,
]).superRefine((value, context) => {
    if (Boolean(value.start.workspaceGeneration) !== Boolean(value.workspaceStorage)) {
        context.addIssue({ code: "custom", path: ["workspaceStorage"], message: "Registered workspace material requires the current persisted workspace proof" });
    }
    if (value.workspaceStorage && value.workspaceStorage.generation !== value.start.workspaceGeneration) {
        context.addIssue({ code: "custom", path: ["workspaceStorage", "generation"], message: "Workspace proof must match the registered start generation" });
    }
    if (Boolean(value.start.continuity) !== Boolean(value.continuitySession)) {
        context.addIssue({ code: "custom", path: ["continuitySession"], message: "Continuity material requires the current persisted session initialization proof" });
    }
    if (value.continuitySession?.workspaceCheckpoint &&
        value.continuitySession.workspaceCheckpoint.checkpoint.version !== value.workspaceStorage?.version) {
        context.addIssue({ code: "custom", path: ["continuitySession", "workspaceCheckpoint"], message: "Workspace recovery must use the current workspace head version" });
    }
    if (value.start.workspaceChange && (value.start.workspaceChange.version !== value.workspaceStorage?.version ||
        value.start.workspaceChange.previousVersion !== value.continuitySession?.checkpointWorkspaceVersion)) {
        context.addIssue({ code: "custom", path: ["start", "workspaceChange"], message: "Workspace notice must match current source and frozen native checkpoint versions" });
    }
});
const legacyRuntimeMaterialHandleSchema = z
    .object({
    continuity: continuityIdentitySchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("legacy_split_v1").default("legacy_split_v1"),
    runtimeAttempt: z.number().int().positive(),
    userId: userIdSchema,
    appId: appIdSchema,
    runId: runIdSchema,
    appActivationId: z.never().optional(),
    providerRef: z.string().min(1).max(500),
    authorityRef: z.string().min(1).max(500),
    modelAuthorityRef: z.string().min(1).max(500).optional(),
    appOwnerCredentialRef: z.never().optional(),
    callbackRef: z.string().min(1).max(500),
    state: z.enum(["ready", "removed"]),
})
    .strict();
const appOwnerRuntimeMaterialHandleSchema = z
    .object({
    continuity: continuityIdentitySchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("app_owner_v1"),
    runtimeAttempt: z.number().int().positive(),
    userId: userIdSchema,
    appId: appIdSchema,
    runId: runIdSchema,
    appActivationId: z.never().optional(),
    providerRef: z.string().min(1).max(500),
    appOwnerCredentialRef: appOwnerCredentialRefSchema,
    mutationJournalGeneration: agentAppMutationJournalGenerationSchema,
    mutationJournalState: z.enum(["initializing", "ready"]),
    authorityRef: z.never().optional(),
    modelAuthorityRef: z.never().optional(),
    callbackRef: z.string().min(1).max(500),
    state: z.enum(["ready", "removed"]),
})
    .strict();
const conversationOnlyRuntimeMaterialHandleSchema = z
    .object({
    continuity: continuityIdentitySchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("conversation_only_v1"),
    runtimeAttempt: z.number().int().positive(),
    userId: userIdSchema,
    appId: appIdSchema,
    runId: runIdSchema,
    appActivationId: z.uuid().optional(),
    conversationExecution: conversationExecutionSchema.optional(),
    providerRef: z.string().min(1).max(500),
    authorityRef: z.never().optional(),
    modelAuthorityRef: z.never().optional(),
    appOwnerCredentialRef: z.never().optional(),
    mutationJournalGeneration: z.never().optional(),
    mutationJournalState: z.never().optional(),
    callbackRef: z.string().min(1).max(500),
    state: z.enum(["ready", "removed"]),
})
    .strict().refine(validConversationMaterial, "A conversation material requires either its legacy activation or an explicit standalone partition");
export const runtimeMaterialHandleSchema = z.union([
    legacyRuntimeMaterialHandleSchema,
    conversationOnlyRuntimeMaterialHandleSchema,
    appOwnerRuntimeMaterialHandleSchema,
]);
const legacyRuntimeMaterialCleanupSchema = z
    .object({
    continuity: continuityIdentitySchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("legacy_split_v1").default("legacy_split_v1"),
    runtimeAttempt: z.number().int().positive(),
    userId: userIdSchema,
    appId: appIdSchema,
    runId: runIdSchema,
    appActivationId: z.never().optional(),
    providerRef: z.string().min(1).max(500),
    authorityRef: z.string().min(1).max(500),
    modelAuthorityRef: z.string().min(1).max(500).optional(),
    appOwnerCredentialRef: z.never().optional(),
    callbackRef: z.string().min(1).max(500),
    preserveContinuation: z.boolean().optional(),
})
    .strict();
const appOwnerRuntimeMaterialCleanupSchema = z
    .object({
    continuity: continuityIdentitySchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("app_owner_v1"),
    runtimeAttempt: z.number().int().positive(),
    userId: userIdSchema,
    appId: appIdSchema,
    runId: runIdSchema,
    appActivationId: z.never().optional(),
    providerRef: z.string().min(1).max(500),
    appOwnerCredentialRef: appOwnerCredentialRefSchema,
    mutationJournalGeneration: agentAppMutationJournalGenerationSchema,
    mutationJournalState: z.enum(["initializing", "ready"]),
    authorityRef: z.never().optional(),
    modelAuthorityRef: z.never().optional(),
    callbackRef: z.string().min(1).max(500),
    preserveContinuation: z.boolean().optional(),
})
    .strict();
const conversationOnlyRuntimeMaterialCleanupSchema = z
    .object({
    continuity: continuityIdentitySchema.optional(),
    schemaVersion: z.literal(1),
    authorityMode: z.literal("conversation_only_v1"),
    runtimeAttempt: z.number().int().positive(),
    userId: userIdSchema,
    appId: appIdSchema,
    runId: runIdSchema,
    appActivationId: z.uuid().optional(),
    conversationExecution: conversationExecutionSchema.optional(),
    providerRef: z.string().min(1).max(500),
    authorityRef: z.never().optional(),
    modelAuthorityRef: z.never().optional(),
    appOwnerCredentialRef: z.never().optional(),
    mutationJournalGeneration: z.never().optional(),
    mutationJournalState: z.never().optional(),
    callbackRef: z.string().min(1).max(500),
    preserveContinuation: z.boolean().optional(),
})
    .strict().refine(validConversationMaterial, "A conversation cleanup requires either its legacy activation or an explicit standalone partition");
function validConversationMaterial(value) {
    return value.conversationExecution
        ? value.conversationExecution.subject.kind === "standalone" &&
            value.conversationExecution.partitionId === value.appId
        : value.appActivationId !== undefined;
}
export const runtimeMaterialCleanupSchema = z.union([
    legacyRuntimeMaterialCleanupSchema,
    conversationOnlyRuntimeMaterialCleanupSchema,
    appOwnerRuntimeMaterialCleanupSchema,
]);
export const runtimeAppOwnerSessionRefreshRequestSchema = z
    .object({
    schemaVersion: z.literal(1),
    runtimeAttempt: z.number().int().positive(),
    userId: userIdSchema,
    // The retained participant is independent of the runtime's storage owner.
    // Older owner-only runtimes omit this field.
    delegatedByUserId: userIdSchema.optional(),
    appId: appIdSchema,
    runId: runIdSchema,
    expectedAppOwnerCredentialRef: appOwnerCredentialRefSchema,
    targetAppOwnerCredentialRef: appOwnerCredentialRefSchema,
    appOwnerCredential: runtimeAppOwnerCredentialMaterialSchema,
})
    .strict()
    .superRefine((value, context) => {
    const material = value.appOwnerCredential;
    if (value.targetAppOwnerCredentialRef !==
        appOwnerCredentialRef(material.credentialId, material.generation)) {
        context.addIssue({
            code: "custom",
            path: ["targetAppOwnerCredentialRef"],
            message: "Refresh target differs from its credential material",
        });
    }
    if (material.delegatedByUserId !== (value.delegatedByUserId ?? value.userId) ||
        material.appId !== value.appId ||
        material.rootRunId !== value.runId ||
        material.runId !== value.runId) {
        context.addIssue({
            code: "custom",
            path: ["appOwnerCredential"],
            message: "Refresh credential differs from the runtime identity",
        });
    }
    const expectedParts = value.expectedAppOwnerCredentialRef.split(":");
    const expectedCredentialId = expectedParts[1];
    const expectedGeneration = Number(expectedParts[2]);
    if (material.rotatedFromCredentialId !== expectedCredentialId ||
        material.generation !== expectedGeneration + 1) {
        context.addIssue({
            code: "custom",
            path: ["appOwnerCredential", "rotatedFromCredentialId"],
            message: "Refresh material must be the next generation after the expected credential",
        });
    }
});
export const runtimeAppOwnerSessionRefreshResultSchema = z
    .object({
    schemaVersion: z.literal(1),
    appOwnerCredentialRef: appOwnerCredentialRefSchema,
    generation: z.number().int().positive(),
    replayed: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if (Number(value.appOwnerCredentialRef.split(":")[2]) !== value.generation) {
        context.addIssue({
            code: "custom",
            path: ["generation"],
            message: "Refresh result generation differs from its credential reference",
        });
    }
});
//# sourceMappingURL=authority.js.map